/**
 * Public QR self check-in for a qualified Verify site: /verify/checkin/<token>.
 *
 * A member scans the poster at the door, writes their name, date of birth and
 * the hours they did, and is done. No account, no app, no Social Security
 * number. The line waits for the site's staff to confirm it before it counts —
 * the page says so, so nobody thinks the hours are already final.
 *
 * Mobile-first, big touch targets, English and Spanish.
 */

import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { publicFetchJson } from '@/lib/authFetch';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { activityLabel } from './shared';

interface SiteInfo {
  site_name: string;
  activity_types: string[];
  contact_name: string | null;
  city: string | null;
}

const today = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const field =
  'w-full rounded-lg border border-v2border bg-surface px-4 py-3 text-base text-ink placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-v2accent';

export default function VerifyCheckinPage({ token }: { token: string }) {
  const { t, language, setLanguage } = useAppTranslation();
  const [site, setSite] = useState<SiteInfo | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone: '',
    activity_type: '',
    service_description: '',
    activity_date: today(),
    hours: '',
    consent: false,
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    publicFetchJson<SiteInfo>(`/api/public/verify/checkin/${encodeURIComponent(token)}`)
      .then((s) => {
        if (cancelled) return;
        setSite(s);
        setForm((f) => ({ ...f, activity_type: s.activity_types[0] || '' }));
        document.title = s.site_name;
      })
      .catch(() => !cancelled && setLoadError(true));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const set = (k: keyof typeof form, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  const hours = Number(form.hours);
  const valid =
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.date_of_birth &&
    form.activity_type &&
    form.activity_date &&
    hours > 0 &&
    hours <= 12 &&
    form.consent;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSending(true);
    setError(null);
    try {
      await publicFetchJson(`/api/public/verify/checkin/${encodeURIComponent(token)}`, {
        method: 'POST',
        jsonBody: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          date_of_birth: form.date_of_birth,
          phone: form.phone.trim() || null,
          activity_type: form.activity_type,
          service_description: form.service_description.trim() || null,
          activity_date: form.activity_date,
          hours,
          consent: true,
        },
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('verify.checkin.failed', 'Something went wrong. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const langToggle = (
    <div className="flex gap-1 text-sm">
      {(['en', 'es'] as const).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => void setLanguage(l)}
          className={`rounded-md px-3 py-1.5 ${language?.startsWith(l) ? 'bg-v2accent-soft text-v2accent-ink' : 'text-ink-500'}`}
        >
          {l === 'en' ? 'English' : 'Español'}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-paper px-4 py-6">
      <div className="mx-auto max-w-md space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-v2accent-ink">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-sm font-medium">{t('verify.checkin.brand', 'Hours check-in')}</span>
          </div>
          {langToggle}
        </div>

        {loadError ? (
          <div className="rounded-xl border border-v2border bg-surface p-6 text-center">
            <AlertCircle className="mx-auto h-8 w-8 text-attention" />
            <p className="mt-3 text-base text-ink">
              {t('verify.checkin.inactive', 'This check-in code is not active. Please ask a staff member for help.')}
            </p>
          </div>
        ) : !site ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-ink-400" />
          </div>
        ) : done ? (
          <div className="rounded-xl border border-v2border bg-surface p-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
            <h1 className="mt-3 font-display text-2xl font-semibold text-ink">
              {t('verify.checkin.thanks', 'Thank you!')}
            </h1>
            <p className="mt-2 text-base text-ink-700">
              {t('verify.checkin.thanks_body', '{{site}} will confirm your hours. Once they do, they count toward your Medicaid work requirement.', {
                site: site.site_name,
              })}
            </p>
            <button
              type="button"
              className="mt-6 w-full rounded-lg border border-v2border px-4 py-3 text-base text-ink"
              onClick={() => {
                setDone(false);
                setForm((f) => ({ ...f, hours: '', service_description: '', consent: false }));
              }}
            >
              {t('verify.checkin.another', 'Check in someone else')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-xl border border-v2border bg-surface p-5">
            <div>
              <h1 className="font-display text-2xl font-semibold text-ink">{site.site_name}</h1>
              <p className="mt-1 text-base text-ink-700">
                {t('verify.checkin.intro', 'Record the hours you did here today. Staff will confirm them.')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-700">{t('verify.first_name', 'First name')}</span>
                <input className={field} autoComplete="given-name" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-700">{t('verify.last_name', 'Last name')}</span>
                <input className={field} autoComplete="family-name" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
              </label>
            </div>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-ink-700">{t('verify.dob', 'Date of birth')}</span>
              <input className={field} type="date" max={today()} value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} />
              <span className="mt-1 block text-xs text-ink-500">
                {t('verify.checkin.dob_why', 'Used only to match these hours to your Medicaid record.')}
              </span>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-ink-700">{t('verify.phone_optional', 'Phone (optional)')}</span>
              <input className={field} type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </label>

            <fieldset>
              <legend className="mb-2 text-sm font-medium text-ink-700">{t('verify.checkin.what', 'What did you do?')}</legend>
              <div className="grid gap-2">
                {site.activity_types.map((a) => (
                  <label
                    key={a}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-base ${
                      form.activity_type === a ? 'border-v2accent bg-v2accent-soft text-v2accent-ink' : 'border-v2border text-ink'
                    }`}
                  >
                    <input
                      type="radio"
                      name="activity_type"
                      className="h-5 w-5"
                      checked={form.activity_type === a}
                      onChange={() => set('activity_type', a)}
                    />
                    {activityLabel(t, a)}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-ink-700">{t('verify.checkin.details', 'Details (optional)')}</span>
              <input
                className={field}
                placeholder={t('verify.checkin.details_ph', 'e.g. Sorted food donations')}
                value={form.service_description}
                onChange={(e) => set('service_description', e.target.value)}
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-700">{t('verify.date', 'Date')}</span>
                <input className={field} type="date" max={today()} value={form.activity_date} onChange={(e) => set('activity_date', e.target.value)} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-700">{t('verify.hours', 'Hours')}</span>
                <input
                  className={field}
                  type="number"
                  inputMode="decimal"
                  min={0.25}
                  max={12}
                  step={0.25}
                  value={form.hours}
                  onChange={(e) => set('hours', e.target.value)}
                />
              </label>
            </div>

            <label className="flex items-start gap-3 text-base text-ink-700">
              <input type="checkbox" className="mt-1 h-5 w-5" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} />
              <span>
                {t(
                  'verify.checkin.consent',
                  'I agree to share these hours with {{site}} and the organization that helps me with Medicaid, so they can be counted.',
                  { site: site.site_name },
                )}
              </span>
            </label>

            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-danger-soft px-3 py-2 text-sm text-ink-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!valid || sending}
              className="w-full rounded-lg bg-v2accent px-4 py-3.5 text-base font-semibold text-white disabled:opacity-50"
            >
              {sending ? t('verify.saving', 'Saving…') : t('verify.checkin.submit', 'Check in')}
            </button>

            <p className="text-center text-xs text-ink-500">
              {t('verify.checkin.no_ssn', 'We never ask for your Social Security number.')}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
